import { AlertTriangle } from 'lucide-react';
import type { DesignerExemplarCoverage } from '../types/designer-exemplar.types';
import { canvasClassLabel } from '../helpers/designer-exemplar.helpers';

interface DesignerExemplarCoveragePanelProps {
  coverage: DesignerExemplarCoverage | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function DesignerExemplarCoveragePanel({
  coverage,
  isLoading,
  isError,
}: DesignerExemplarCoveragePanelProps) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-gray-300 bg-gray-50 p-4 text-sm text-gray-500" role="status">
        Loading coverage…
      </div>
    );
  }

  if (isError || !coverage) {
    return (
      <div className="rounded-md border border-red-101 bg-red-101 p-4 text-sm text-red-700" role="alert">
        Could not load exemplar coverage.
      </div>
    );
  }

  const classes = Array.isArray(coverage.classes) ? coverage.classes : [];

  return (
    <section className="rounded-md border border-gray-300 bg-gray-50 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-black">Corpus coverage</h3>
          <p className="text-xs text-gray-500">
            Target of {coverage.target_per_class} active exemplars per canvas class.
          </p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {classes.map((entry, index) => {
          const label = entry.display_name || canvasClassLabel(entry.canvas_class);
          const isThin = entry.is_thin;
          return (
            <div
              key={entry.canvas_class || `class-${index}`}
              className={`rounded-md border bg-white p-3 ${
                isThin ? 'border-amber-400' : 'border-gray-300'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-medium capitalize text-black">{label}</div>
                  <div className="mt-1 text-2xl font-semibold text-black">
                    {entry.active_count}
                    <span className="text-sm font-normal text-gray-500">
                      {' '}
                      / {coverage.target_per_class}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {entry.count} total ({entry.active_count} active)
                  </div>
                </div>
                {isThin ? (
                  <AlertTriangle className="size-4 shrink-0 text-amber-600" aria-label="Thin coverage" />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
